import { FormControl, Grid, Input, InputLabel, Select, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../AppRoutes";
import AppFooter from "../components/AppFooter";
import AppHeader from "../components/AppHeader";
import BodyContainer from "../components/BodyContainer";
import FooterButtons from "../components/FooterButtons";
import DecimalInput from "../components/form/DecimalInput";
import IntegerInput from "../components/form/IntegerInput";
import { crops, forestry } from "../config/burns.constants";
import { setFuelLoad } from "../store/submission.slice";

const FuelLoading = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const data = useSelector(state => state.submission.fuelLoad);

	const [fuelLoadVals] = useState(['Low', 'Medium', 'Heavy'])
	const [values, setValues] = useState({
		type: data.cropType || '',
		fuelRange: data.fuelLoadingRange, // 0: Low, 1: Medium, 2: High
		totalAcres: data.totalAcres !== 0 ? data.totalAcres.toString() : '',
		fuelLoading: data.totalTonnage || 0 // readonly
	})

	const [cropUnitValue, setCropUnitValue] = useState('');

	const onNext = () => {
		const cropCategory = crops.find(c => c.name === values.type) ? 'crop' : (forestry.find(f => f.name === values.type) ? 'forestry' : '');

		dispatch(setFuelLoad({
			cropCategory,
			cropType: values.type,
			fuelLoadingRange: values.fuelRange,
			totalAcres: values.totalAcres,
			totalTonnage: values.fuelLoading
		}));
		navigate(appRoutes.SmokeSensitiveAreas);
	}

	const onValueChange = v => {
		const newVals = { ...values, ...v };
		const fuelLoading = calculateTotalFuelLoading(newVals);

		const infoFound = crops.concat(forestry).find(c => c.name === newVals.type);
		const cropUnitDesc = infoFound ? infoFound.fuelLoadingDescription : null
		setCropUnitValue(cropUnitDesc ? cropUnitDesc[newVals.fuelRange] : null);

		setValues({ ...newVals, fuelLoading});
	}

	/**
	 * Calculates total available fuel loading: fuel loading range in tons/acre * total acres
	 */
	const calculateTotalFuelLoading = useCallback((vals) => {
		const found = crops.concat(forestry).find(c => c.name === vals.type);
		if (!found) return 0.0;

		const fuelLoadingRange = found.fuelLoadingRange[vals.fuelRange]
		return (fuelLoadingRange * vals.totalAcres) || 0.0;
	}, []);

	return (
		<>
			<AppHeader />
			<BodyContainer hasFooter={false} hasHeader={true}>
				<Grid item xs={1} mb={4} mt={2} width="100%">
					<Typography variant="title" component="h1">Determine Total Available Fuel Loading</Typography>
					<Typography variant="subtitle2" component="h2">
						The emission data needed by the ADA Dispatch Center is the consumption of "available" fuels
						for the entire burn. (Available fuel loading per acre x total acres to be burned = total available fuel
						loading for the burn).
					</Typography>
				</Grid>
				<Grid item container xs={9} width="100%">
					<div style={{ height: "70%", display: 'flex', flexDirection: 'column', width: '100%', justifyContent: 'space-between' }}>
						<FormControl variant="standard" required fullWidth mb={2}>
							<InputLabel shrink htmlFor="crop">Field Crop/Forestry</InputLabel>
							<Select
								native
								id="crop"
								label="Select"
								value={values.type}
								onChange={v => onValueChange({ type: v.target.value })}>
								<option aria-label="None" value="" >Select a Fuel</option>
								<optgroup label="Crop">
									{crops.map(crop => (
										<option key={crop.name} value={crop.name}>{crop.name}</option>
									))}
								</optgroup>
								<optgroup label="Forestry">
									{forestry.map(fore => (
										<option key={fore.name} value={fore.name}>{fore.name}</option>
									))}
								</optgroup>
							</Select>
						</FormControl>
						<FormControl variant="standard" required fullWidth disabled={!values.type} mb={2}>
							<InputLabel shrink htmlFor="range">Fuel Loading</InputLabel>
							<Select
								native
								id="range"
								label="select fuel loading"
								placeholder="select fuel loading"
								value={values.fuelRange}
								onChange={v => onValueChange({ fuelRange: v.target.value })}>
								<option aria-label="None" value={-1} >Select fuel loading</option>
								{fuelLoadVals.map((r, idx) => (
									<option key={r} aria-label={r} value={idx}>{r}</option>
								))}
							</Select>
							<Typography variant="subtitle2">
								{cropUnitValue}
							</Typography>
						</FormControl>
						<FormControl variant="standard" required fullWidth disabled={values.fuelRange == null} mb={2}>
							<InputLabel shrink htmlFor="acres">Total Acres</InputLabel>
							<IntegerInput
								id="acres"
								value={values.totalAcres}
								max={999999}
								onChange={(v) => onValueChange({ totalAcres: v.target.value })} />
						</FormControl>
						<FormControl variant="standard" required fullWidth>
							<InputLabel shrink htmlFor="total">Total Tonnage</InputLabel>
							<DecimalInput readOnly disabled value={values.fuelLoading} id="total" />
						</FormControl>
					</div>
				</Grid>
				<Grid item width="100%">
					<FooterButtons onNext={onNext} onBack={() => navigate(appRoutes.SelectLocation)} nextDisabled={(!values.type.length) || (values.fuelRange == -1) || !values.totalAcres} />
				</Grid>
			</BodyContainer>
			{/* <AppFooter /> */}
		</>
	)
}

export default FuelLoading;